import { useState } from "react";
import type { FormEvent } from "react";
import { Eye, EyeOff, Lock, Mail, ShieldCheck } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { MfaRequiredError, useAuth } from "../auth/OAuthContext";
import { API_BASE } from "../api/base";
import { AuthScreen } from "../components/AuthScreen";
import { OtpCodeInput } from "../components/OtpCodeInput";

const ENTRA_SSO_ENABLED = import.meta.env.VITE_ENTRA_SSO_ENABLED === "true";

function normalizeMfaCode(value: string): string {
  return value.replace(/\D/g, "").slice(0, 6);
}

export function Login() {
  const { login, submitMfaCode, mfaPending, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof MfaRequiredError) {
        // Expected: form now shows the MFA code field.
        return;
      }
      setError(err instanceof Error ? err.message : "Login failed");
    }
  }

  async function handleMfa(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const normalizedCode = normalizeMfaCode(code);
    if (normalizedCode.length !== 6) {
      setError("Enter the 6-digit code from your authenticator app");
      return;
    }
    try {
      await submitMfaCode(normalizedCode);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code");
    }
  }

  if (mfaPending) {
    return (
      <AuthScreen>
        <form onSubmit={handleMfa} className="w-full flex flex-col gap-5">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold self-start">
            <ShieldCheck size={16} aria-hidden="true" />
            <span>Secure sign-in</span>
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight font-display">
              Verify it's you
            </h1>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
              Enter the six-digit code from your authenticator app to finish signing in.
            </p>
          </div>
          <OtpCodeInput
            id="login-mfa-code"
            label="Verification code"
            value={code}
            onChange={(nextCode) => {
              setCode(nextCode);
              setError(null);
            }}
            invalid={!!error}
            autoFocus
          />
          <p className="text-xs text-slate-400">Open your authenticator app to find your current code.</p>
          {error && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full min-h-[46px] flex items-center justify-center font-bold text-sm text-white bg-[#012699] hover:bg-[#0b2981] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all shadow-sm cursor-pointer"
          >
            {loading ? "Verifying..." : "Verify and sign in"}
          </button>
        </form>
      </AuthScreen>
    );
  }

  function handleMicrosoftSignIn() {
    window.location.assign(`${API_BASE}/entra/login?returnTo=${encodeURIComponent("/dashboard")}`);
  }

  return (
    <AuthScreen>
      <form onSubmit={handleLogin} className="w-full flex flex-col gap-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight font-display">
            MEATrix OrderFlow
          </h1>
          <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
            Sign in to review, route, attest, and seal physician order documents.
          </p>
        </div>

        <label className="flex flex-col gap-1.5 text-xs font-bold text-slate-700">
          <span>Email</span>
          <div className="relative flex items-center min-h-[46px] px-3.5 border border-slate-300 rounded-xl focus-within:border-[#012699] focus-within:ring-3 focus-within:ring-[#012699]/15 transition-all bg-white">
            <Mail size={18} className="text-slate-400 shrink-0 mr-2.5" aria-hidden="true" />
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-0 outline-none text-sm text-slate-900 placeholder:text-slate-400"
              autoFocus
              required
            />
          </div>
        </label>

        <label className="flex flex-col gap-1.5 text-xs font-bold text-slate-700">
          <span>Password</span>
          <div className="relative flex items-center min-h-[46px] px-3.5 border border-slate-300 rounded-xl focus-within:border-[#012699] focus-within:ring-3 focus-within:ring-[#012699]/15 transition-all bg-white">
            <Lock size={18} className="text-slate-400 shrink-0 mr-2.5" aria-hidden="true" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-0 outline-none text-sm text-slate-900 placeholder:text-slate-400"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              className="p-1 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
            </button>
          </div>
        </label>

        {error && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full min-h-[46px] flex items-center justify-center font-bold text-sm text-white bg-[#012699] hover:bg-[#0b2981] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all shadow-sm cursor-pointer"
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>

        {ENTRA_SSO_ENABLED && (
          <button
            type="button"
            className="w-full min-h-[46px] flex items-center justify-center font-bold text-sm text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl transition-all cursor-pointer"
            onClick={handleMicrosoftSignIn}
          >
            Sign in with Microsoft
          </button>
        )}

        <p className="text-center text-xs text-slate-500 mt-2">
          New nurse?{" "}
          <Link to="/register" className="font-bold text-[#012699] hover:underline underline-offset-4">
            Register with your NPI
          </Link>
        </p>
      </form>
    </AuthScreen>
  );
}
